import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
  onBecomeUnobserved,
  reaction,
  runInAction,
} from "mobx";
import { liveQuery, type Subscription } from "dexie";

import { BaseSyncModel } from "@/store/sync/BaseSyncModel";
import { SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import { WithAppStore } from "@/store/types";
import {
  AudioRecordingModelData,
  AudioRecordingRecordingStatus,
  AudioRecordingProcessingStatus,
  AudioRecordingUploadStatus,
  AudioRecordingAppEvent,
  AudioRecordingAppEventKind,
  AudioRecordingStrategy,
  getAudioRecordingStrategy,
} from "./types";
import {
  calculateRecordingDuration,
  getMeetingHandoffPreviousAudioRecordingId,
  getMeetingHandoffSuccessorAudioRecordingId,
  isPipelineDeferredToMeetingHandoffSuccessor,
} from "./app-events";
import type { LocalAudioRecordingTranscriptTurnRecord } from "@/domains/dexie-db/models";
import type { Uuid } from "@/domains/global/identifiers";
import { clientEnvModule } from "@/modules/client-env";
import type { MediaRecorderTranscriptPartialTurn } from "@/store/audio/recording";

type ConstructorParams = {
  id: string;
  data: SyncUpdateValue<AudioRecordingModelData>;
} & WithAppStore;

type DurationSubscription = Subscription | (() => void);

export class AudioRecordingObservable extends BaseSyncModel<AudioRecordingModelData> {
  public modelKind = SyncModelKind.AudioRecording;
  private durationSubscription?: DurationSubscription;
  private transcriptSubscription?: Subscription;
  durationMs: number = 0;
  private localTranscriptTurns: LocalAudioRecordingTranscriptTurnRecord[] = [];

  constructor({ id, data, store }: ConstructorParams) {
    super({ id, data, store });

    makeObservable<
      AudioRecordingObservable,
      "durationSubscription" | "transcriptSubscription" | "resetDuration" | "localTranscriptTurns"
    >(this, {
      associatedClientId: true,
      modelKind: false,
      recordingStatus: computed,
      uploadStatus: computed,
      processingStatus: computed,
      recordingStrategy: computed,
      locallyCreatedAt: computed,
      locallyModifiedAt: computed,
      valueJson: computed,
      appEvents: computed,
      hasAppEvent: false,
      wasTranscriptionCompromised: computed,
      associatedNoteId: computed,
      meetingHandoffPreviousAudioRecordingId: computed,
      meetingHandoffSuccessorAudioRecordingId: computed,
      isPipelineDeferredToMeetingHandoffSuccessor: computed,
      durationMs: observable,
      formattedDuration: computed,
      subscribeToDuration: action,
      unsubscribeFromDuration: action,
      durationSubscription: observable.ref,
      transcriptTurns: computed,
      liveTranscriptPartials: computed,
      subscribeToTranscripts: action,
      unsubscribeFromTranscripts: action,
      transcriptSubscription: observable.ref,
      localTranscriptTurns: observable.shallow,
      resetDuration: action,
      uploadProgressPercent: computed,
      uploadErrorMessage: computed,
      transformErrorMessage: computed,
      isTransforming: computed,
      hasActiveSession: computed,
      isRecording: computed,
      isUploading: computed,
      isProcessing: computed,
      hasFinishedProcessing: computed,
      hasFailedProcessing: computed,
      hasFailedUploading: computed,
      hasFinishedRecording: computed,
      hasStartedRecording: computed,
      hasBeenCompleted: computed,
      hasNotBeenCompleted: computed,
      trashedAt: computed,
      isTrashed: computed,
      hasValidAssociatedNote: computed,
      isFromCurrentClientDevice: false,
    });

    onBecomeObserved(this, "durationMs", () => this.subscribeToDuration());
    onBecomeUnobserved(this, "durationMs", () => this.unsubscribeFromDuration());

    onBecomeObserved(this, "transcriptTurns", () => this.subscribeToTranscripts());
    onBecomeUnobserved(this, "transcriptTurns", () => this.unsubscribeFromTranscripts());
  }

  get hasActiveSession(): boolean {
    return this.store.audio.activeRecordingId === this.id;
  }

  get hasValidAssociatedNote(): boolean {
    const noteId = this.associatedNoteId as Uuid | undefined;
    if (!noteId) return false;

    const note = this.store.notes.getNoteObservableById({ noteId });
    if (note) {
      return !note.isDeleted && !note.isTrashed;
    }

    if (this.store.notes.doesNotExist(noteId)) {
      return false;
    }

    return true;
  }

  get hasBeenCompleted(): boolean {
    return this.hasFinishedProcessing;
  }

  get hasNotBeenCompleted(): boolean {
    return !this.hasBeenCompleted;
  }

  get isRecording(): boolean {
    return this.recordingStatus === AudioRecordingRecordingStatus.IN_PROGRESS;
  }

  get isUploading(): boolean {
    return this.uploadStatus === AudioRecordingUploadStatus.IN_PROGRESS;
  }

  get hasFailedUploading(): boolean {
    return this.uploadStatus === AudioRecordingUploadStatus.ERROR;
  }

  get isProcessing(): boolean {
    return this.processingStatus === AudioRecordingProcessingStatus.IN_PROGRESS;
  }

  get hasFinishedProcessing(): boolean {
    return this.processingStatus === AudioRecordingProcessingStatus.COMPLETED;
  }

  get hasFailedProcessing(): boolean {
    return this.processingStatus === AudioRecordingProcessingStatus.ERROR;
  }

  get trashedAt(): string | null {
    return this.modelData.trashed_at ?? null;
  }

  get isTrashed(): boolean {
    return this.trashedAt !== null;
  }

  get hasFinishedRecording(): boolean {
    return this.recordingStatus === AudioRecordingRecordingStatus.COMPLETED;
  }

  get hasStartedRecording(): boolean {
    return this.recordingStatus !== AudioRecordingRecordingStatus.INITIAL;
  }

  get recordingStatus(): AudioRecordingModelData["recording_status"] {
    return this.modelData.recording_status;
  }

  get uploadStatus(): AudioRecordingModelData["upload_status"] {
    return this.modelData.upload_status;
  }

  get processingStatus(): AudioRecordingModelData["processing_status"] {
    return this.modelData.processing_status;
  }

  get recordingStrategy(): AudioRecordingStrategy {
    return getAudioRecordingStrategy({ modelData: this.modelData });
  }

  get locallyCreatedAt(): string {
    return this.modelData.locally_created_at;
  }

  get locallyModifiedAt(): string {
    return this.modelData.locally_modified_at;
  }

  get valueJson(): AudioRecordingModelData["value_json"] | undefined {
    return this.modelData.value_json;
  }

  /**
   * Returns the app_events array from value_json, typed as AudioRecordingAppEvent[].
   * These events track the lifecycle of the recording (start, pause, resume, complete, errors, etc.)
   */
  get appEvents(): AudioRecordingAppEvent[] {
    const events = this.modelData.value_json?.app_events;
    if (!Array.isArray(events)) return [];
    return events as AudioRecordingAppEvent[];
  }

  /**
   * Checks if this recording has an app event of the specified kind.
   */
  hasAppEvent(kind: AudioRecordingAppEventKind): boolean {
    return this.appEvents.some(event => event.kind === kind);
  }

  /**
   * Returns true if transcription was compromised during this recording
   * (i.e., max reconnection attempts were exceeded).
   */
  get wasTranscriptionCompromised(): boolean {
    return this.hasAppEvent(AudioRecordingAppEventKind.TRANSCRIPTION_COMPROMISED);
  }

  get associatedNoteId(): string | undefined {
    return this.modelData.associated_note_id ?? undefined;
  }

  get meetingHandoffPreviousAudioRecordingId(): Uuid | null {
    return getMeetingHandoffPreviousAudioRecordingId({ modelData: this.modelData });
  }

  get meetingHandoffSuccessorAudioRecordingId(): Uuid | null {
    return getMeetingHandoffSuccessorAudioRecordingId({ modelData: this.modelData });
  }

  get isPipelineDeferredToMeetingHandoffSuccessor(): boolean {
    return isPipelineDeferredToMeetingHandoffSuccessor({ modelData: this.modelData });
  }

  get associatedClientId(): string | undefined {
    return this.modelData.associated_client_id ?? undefined;
  }

  get formattedDuration(): string {
    const totalMs = this.durationMs;

    if (!totalMs) return "00:00";

    const minutes = Math.floor(totalMs / 60000);
    const seconds = Math.floor((totalMs % 60000) / 1000);

    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }

  get transcriptTurns(): LocalAudioRecordingTranscriptTurnRecord[] {
    return this.localTranscriptTurns;
  }

  get liveTranscriptPartials(): MediaRecorderTranscriptPartialTurn[] {
    return this.store.audio.recording.getLiveTranscriptPartials({
      audioRecordingId: this.id,
    });
  }

  subscribeToDuration() {
    this.unsubscribeFromDuration();

    if (this.recordingStrategy === AudioRecordingStrategy.DesktopArtifact) {
      this.durationSubscription = reaction(
        () => ({
          liveDurationMs: this.store.audio.recording.getLiveDurationMs({
            audioRecordingId: this.id,
          }),
          recordingStatus: this.recordingStatus,
          locallyModifiedAt: this.locallyModifiedAt,
        }),
        ({ liveDurationMs, recordingStatus }) => {
          if (recordingStatus === AudioRecordingRecordingStatus.IN_PROGRESS) {
            runInAction(() => {
              this.durationMs = liveDurationMs;
            });
            return;
          }

          const durationResult = calculateRecordingDuration({
            modelData: this.modelData,
          });

          runInAction(() => {
            this.durationMs = durationResult.success
              ? Math.max(0, Math.round(durationResult.duration.as("milliseconds")))
              : liveDurationMs;
          });
        },
        { fireImmediately: true }
      );

      return;
    }

    this.durationSubscription = liveQuery(async () => {
      const artifactSnapshot =
        await this.store.dexie.repositories.webAudioArtifacts.getAudioArtifactSnapshot({
          audioRecordingId: this.id,
        });

      return artifactSnapshot?.durationMs ?? 0;
    }).subscribe({
      next: totalDurationMs => {
        runInAction(() => {
          this.durationMs = totalDurationMs ?? 0;
        });
      },
      error: () => {
        this.resetDuration();
      },
    });
  }

  unsubscribeFromDuration() {
    if (typeof this.durationSubscription === "function") {
      this.durationSubscription();
    } else {
      this.durationSubscription?.unsubscribe();
    }
    this.durationSubscription = undefined;
  }

  subscribeToTranscripts() {
    this.unsubscribeFromTranscripts();

    this.transcriptSubscription = liveQuery(async () => {
      const turns = await this.store.dexie.repositories.localAudioRecordingTranscriptTurns.table
        .where({ associatedAudioRecordingId: this.id })
        .sortBy("transcriptTurnIndex");

      return turns;
    }).subscribe({
      next: turns => {
        const persistedTurns = turns ?? [];

        runInAction(() => {
          this.localTranscriptTurns = persistedTurns;
          this.store.audio.recording.reconcileLiveTranscriptPartialsWithPersistedTurns({
            audioRecordingId: this.id,
            persistedTurnOrders: persistedTurns.map(turn => turn.transcriptTurnIndex),
          });
        });
      },
      error: () => {
        runInAction(() => {
          this.localTranscriptTurns = [];
        });
      },
    });
  }

  unsubscribeFromTranscripts() {
    this.transcriptSubscription?.unsubscribe();
    this.transcriptSubscription = undefined;
  }

  private resetDuration() {
    this.durationMs = 0;
  }

  get uploadProgressPercent(): number | null {
    return this.store.audio.getUploadStateForRecording({ audioRecordingId: this.id })
      .uploadProgressPercent;
  }

  get uploadErrorMessage(): string | null {
    return this.store.audio.getUploadStateForRecording({ audioRecordingId: this.id }).errorMessage;
  }

  get transformErrorMessage(): string | null {
    return this.store.audio.getTransformStateForRecording({ audioRecordingId: this.id })
      .errorMessage;
  }

  get isTransforming(): boolean {
    return this.store.audio.getTransformStateForRecording({ audioRecordingId: this.id })
      .isTransforming;
  }

  async isFromCurrentClientDevice(): Promise<boolean> {
    const { clientGroupId } = await clientEnvModule.clientGroupId();
    return this.associatedClientId === clientGroupId;
  }
}
